/**
 * Execution status classification for agent_logs / agent_test_runs rows.
 *
 * A TABLE, not an if-chain, and no branch that resolves to success by default
 * (orchestrator ruling msg-qwif1z0b6k on PR #6315). Every value is anchored on the
 * backend's own write sites at next 7b4ce42f391, or on rows measured on db2 on
 * 2026-09-17 when no writer exists in this repo at that sha. The PUT
 * /api/logs/:traceId route (routes/logs-postgresql.js:337-353) accepts any status
 * string from its body, so the vocabulary is open-ended: a value not in this table
 * classifies as 'unknown' and the caller must fail loud, never assume.
 */
export type ExecutionStatusClass = 'nonterminal' | 'success' | 'failure';

export interface ExecutionStatusEntry {
  cls: ExecutionStatusClass;
  /** file:line of the write site, or the measurement, that puts this value in the column */
  source: string;
}

export const EXECUTION_STATUS_TABLE: Readonly<Record<string, ExecutionStatusEntry>> = Object.freeze({
  pending: {
    cls: 'nonterminal',
    source: 'backend-merged/routes/agents.js:432 (test run insert); routes/logs-postgresql.js:284 (POST /api/logs default); scripts/add-agent-logs-columns.sql:24 (agent_test_runs.status DEFAULT); 33 agent_test_runs rows on db2',
  },
  submitted: {
    cls: 'nonterminal',
    source: 'measured: 27 agent_test_runs rows on db2 2026-09-17; no writer in this repo at 7b4ce42f391',
  },
  processing: {
    cls: 'nonterminal',
    source: 'backend-merged/services/automatic-semantic-analysis.js:522; services/page-role-assignment-service.js:114; routes/logs-postgresql.js:54 treats it as a placeholder; 12 agent_logs rows on db2',
  },
  completed: {
    cls: 'success',
    source: 'backend-merged/routes/agents.js:452; routes/logs-postgresql.js:68,152,252 (placeholder merged with a response); scripts/run-intent-classifier-agent.js:152; 289,883 agent_logs + 172 agent_test_runs rows on db2. services/update-agent-logs-postgresql.js:22 WAS a writer of this value and no longer is: task-1urp0gi3tm replaced its `data.status || \'completed\'` with the status the response reports, or NULL when it reports none, so that path now produces an unclassified row instead of a fabricated success.',
  },
  success: {
    cls: 'success',
    source: 'measured: 1,635 agent_logs rows on db2 2026-09-17; the writer is outside backend-merged at 7b4ce42f391',
  },
  failed: {
    cls: 'failure',
    source: 'backend-merged/routes/agents.js:467 (test run update on a thrown test)',
  },
  error: {
    cls: 'failure',
    source: 'backend-merged/scripts/run-intent-classifier-agent.js:159; 135 agent_logs + 23 agent_test_runs rows on db2',
  },
  timeout: {
    cls: 'failure',
    source: 'no writer and no row at 7b4ce42f391; classified a failure by orchestrator ruling msg-qwif1z0b6k',
  },
});

export type ExecutionStatusClassification =
  | { cls: ExecutionStatusClass; value: string; source: string }
  | { cls: 'unknown'; value: string };

/** Classify a status value from a row. Case-insensitive, whitespace-trimmed. No default branch. */
export function classifyExecutionStatus(status: unknown): ExecutionStatusClassification {
  const value = typeof status === 'string' ? status.trim().toLowerCase() : '';
  const entry = Object.prototype.hasOwnProperty.call(EXECUTION_STATUS_TABLE, value) ? EXECUTION_STATUS_TABLE[value] : undefined;
  if (!entry) return { cls: 'unknown', value: typeof status === 'string' ? status : String(status) };
  return { cls: entry.cls, value, source: entry.source };
}
